import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import type { CartItem } from '@/modules/cart/domain/entities/CartItem';
import type { CartItemDTO } from '@/modules/cart/application/dtos/CartDTO';
import { HttpCartRepository } from '@/modules/cart/infrastructure/repositories/HttpCartRepository';
import { LocalStorageCartAdapter } from '@/modules/cart/infrastructure/storage/LocalStorageCartAdapter';
import { AddToCartUseCase } from '@/modules/cart/application/use-cases/AddToCartUseCase';
import { UpdateCartItemUseCase } from '@/modules/cart/application/use-cases/UpdateCartItemUseCase';
import { RemoveFromCartUseCase } from '@/modules/cart/application/use-cases/RemoveFromCartUseCase';
import { useToastStore } from './toast';
import { useAuthStore } from './auth';
import { getApiErrorMessage } from '@/utils/apiError';
import { usePromotions } from '@/composables/usePromotions';
import { debounce } from '@/utils/debounce';
import type { Promotion, PromotionValidationRequest } from '@/types';
import { i18n } from '@/plugins/i18n';

const toNumber = (value: number | string): number => {
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const repository = new HttpCartRepository();
const storage = new LocalStorageCartAdapter();
const addUseCase = new AddToCartUseCase(repository);
const updateUseCase = new UpdateCartItemUseCase(repository);
const removeUseCase = new RemoveFromCartUseCase(repository);
const APPLIED_COUPON_KEY = 'tfs_applied_coupon_code';

const mapCouponError = (error: unknown): string => {
  const message = getApiErrorMessage(error, '').toLowerCase();

  if (message.includes('not found') || message.includes('promotionnotfoundexception')) {
    return i18n.global.t('cart.coupon.error.invalid');
  }
  if (message.includes('expired')) {
    return i18n.global.t('cart.coupon.error.expired');
  }
  if (message.includes('usage limit')) {
    return i18n.global.t('cart.coupon.error.limitReached');
  }
  if (message.includes('not applicable')) {
    return i18n.global.t('cart.coupon.error.notApplicable');
  }
  if (message.includes('user not eligible')) {
    return i18n.global.t('cart.coupon.error.notEligible');
  }

  return i18n.global.t('cart.coupon.error.invalid');
};

export const useCartStore = defineStore('cart', () => {
  const items = ref<CartItem[]>([]);
  const subtotal = ref(0);
  const total = ref(0);
  const discountAmount = ref(0);
  const appliedCouponCode = ref<string | null>(null);
  const appliedPromotion = ref<Promotion | null>(null);
  const couponLoading = ref(false);
  const couponError = ref<string | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const toastStore = useToastStore();
  const { validatePromotion, getActivePromotions } = usePromotions();

  const recalculateTotal = () => {
    total.value = Math.max(subtotal.value - discountAmount.value, 0);
  };

  const persistAppliedCoupon = () => {
    if (appliedCouponCode.value) {
      localStorage.setItem(APPLIED_COUPON_KEY, appliedCouponCode.value);
      return;
    }

    localStorage.removeItem(APPLIED_COUPON_KEY);
  };

  const getValidationPayload = (code: string): PromotionValidationRequest => {
    return {
      code,
      subtotal: subtotal.value,
      strict_user_validation: true,
      cart_items: items.value.map((item) => ({
        product_entity_type: item.productEntityType,
        product_entity_id: item.productEntityId,
        quantity: item.quantity,
        unit_price: item.productData.price,
        subtotal: item.subtotal,
      })),
    };
  };

  const itemsCount = computed(() => items.value.reduce((sum, item) => sum + item.quantity, 0));
  const isEmpty = computed(() => items.value.length === 0);

  // Initialize from LocalStorage
  const init = () => {
    const cachedItems = storage.load();
    const cachedCouponCode = localStorage.getItem(APPLIED_COUPON_KEY);

    appliedCouponCode.value = cachedCouponCode || null;

    if (cachedItems.length > 0) {
      // Map back to entities, tolerando formas legacy (product_info vs product)
      items.value = cachedItems.map(dto => {
        const productSource = (dto.product_info ?? dto.product ?? {
          name: 'Product',
          image: null,
          is_digital: undefined,
        }) as { name: string; image: string | null; is_digital?: boolean };

        const isDigital = productSource.is_digital ?? (dto.product_entity_type === 'gallery');
        const subtotalNumber = toNumber(dto.subtotal);

        return {
          id: dto.id,
          productEntityType: dto.product_entity_type,
          productEntityId: dto.product_entity_id,
          quantity: dto.quantity,
          productData: {
            name: productSource.name,
            price: toNumber(dto.unit_price),
              image: productSource.image ?? null,
              isDigital,
            },
            isPurchable: dto.is_purchable ?? true,
            subtotal: subtotalNumber,
            formattedSubtotal: `$${subtotalNumber.toFixed(2)}`,
          } as unknown as CartItem;
        });
      
      subtotal.value = items.value.reduce((sum, i) => sum + i.subtotal, 0);
      recalculateTotal();
    }
  };

  const loadCart = async () => {
    loading.value = true;
    try {
      const authStore = useAuthStore();
      if (!authStore.token) {
        // Guest user - cart is already loaded from localStorage in init()
        // Just ensure state is correct
        if (items.value.length === 0) {
          // Try to load from localStorage again
          init();
        }

        if (appliedCouponCode.value) {
          await revalidateCouponIfNeeded();
        }

        return;
      }
      
      // Authenticated user - sync with backend
      const data = await repository.getCart();
      updateState(data);

      const nonPurchableItems = data.items.filter((item) => item.isPurchable === false);
      if (nonPurchableItems.length > 0) {
        for (const item of nonPurchableItems) {
          await repository.removeItem(item.id);
        }

        const sanitizedCart = await repository.getCart();
        updateState(sanitizedCart);

        const itemLabel = nonPurchableItems.length === 1 ? 'item' : 'items';
        toastStore.addToast(
          `${nonPurchableItems.length} ${itemLabel} were removed because you already own them.`,
          'info'
        );
      }

      if (appliedCouponCode.value) {
        await revalidateCouponIfNeeded();
      }
    } catch (err: unknown) {
      error.value = getApiErrorMessage(err, 'Failed to sync cart');
      // If we have cached items, we don't show a big error, just a toast
      if (items.value.length > 0) {
        toastStore.addToast('Using offline cart version', 'info');
      }
    } finally {
      loading.value = false;
    }
  };

  const addItem = async (entityType: string, entityId: string, quantity: number = 1, productInfo?: { name: string; price: number; image?: string | null; isDigital?: boolean }) => {
    loading.value = true;
    error.value = null;
    
    const authStore = useAuthStore();
    const previousState = { items: [...items.value], subtotal: subtotal.value, total: total.value };

    try {
      if (!authStore.token) {
        // Guest user - add to local cart only
        const existingItem = items.value.find(i => 
          i.productEntityType === entityType && i.productEntityId === entityId
        );
        
        if (existingItem) {
          existingItem.quantity += quantity;
          existingItem.subtotal = existingItem.quantity * existingItem.productData.price;
        } else {
          const price = productInfo?.price ?? 0;
          const newItem: CartItem = {
            id: `local_${Date.now()}`,
            productEntityType: entityType,
            productEntityId: entityId,
            quantity,
            productData: {
              name: productInfo?.name ?? 'Product',
              price,
              image: productInfo?.image ?? null,
              isDigital: productInfo?.isDigital ?? (entityType === 'gallery'),
            },
            isPurchable: true,
            subtotal: quantity * price,
            formattedSubtotal: `$${(quantity * price).toFixed(2)}`,
          } as CartItem;
          items.value.push(newItem);
        }
        
        // Recalculate totals
        subtotal.value = items.value.reduce((sum, i) => sum + i.subtotal, 0);
        recalculateTotal();
        
        // Save to localStorage
        updateState({ items: items.value, subtotal: subtotal.value, total: total.value });
        toastStore.addToast('Item added to cart');
      } else {
        // Authenticated user - sync with backend
        const data = await addUseCase.execute(entityType, entityId, quantity);
        updateState(data);
        toastStore.addToast('Item added to cart');
      }
    } catch (err: unknown) {
      error.value = getApiErrorMessage(err, 'Failed to add item');
      updateState(previousState);
      toastStore.addToast(error.value!, 'error');
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    const previousState = { items: [...items.value], subtotal: subtotal.value, total: total.value };
    const authStore = useAuthStore();

    // Optimistic update
    const item = items.value.find(i => i.id === itemId);
    if (item) {
      item.quantity = quantity;
      item.subtotal = quantity * item.productData.price;
    }

    try {
      if (!authStore.token) {
        // Guest user - update locally only
        subtotal.value = items.value.reduce((sum, i) => sum + i.subtotal, 0);
        recalculateTotal();
        updateState({ items: items.value, subtotal: subtotal.value, total: total.value });
      } else {
        // Authenticated user - sync with backend
        const data = await updateUseCase.execute(itemId, quantity);
        updateState(data);
      }
    } catch (err: unknown) {
      error.value = getApiErrorMessage(err, 'Failed to update quantity');
      updateState(previousState);
      toastStore.addToast(error.value!, 'error');
      throw err;
    }
  };

  const removeItem = async (itemId: string) => {
    const previousState = { items: [...items.value], subtotal: subtotal.value, total: total.value };
    const authStore = useAuthStore();
    
    // Optimistic removal
    items.value = items.value.filter(i => i.id !== itemId);
    
    try {
      if (!authStore.token) {
        // Guest user - remove locally only
        subtotal.value = items.value.reduce((sum, i) => sum + i.subtotal, 0);
        recalculateTotal();
        updateState({ items: items.value, subtotal: subtotal.value, total: total.value });
        toastStore.addToast('Item removed from cart');
      } else {
        // Authenticated user - sync with backend
        const data = await removeUseCase.execute(itemId);
        updateState(data);
        toastStore.addToast('Item removed from cart');
      }
    } catch (err: unknown) {
      error.value = getApiErrorMessage(err, 'Failed to remove item');
      updateState(previousState);
      toastStore.addToast(error.value!, 'error');
      throw err;
    }
  };

  const clearCart = () => {
    items.value = [];
    subtotal.value = 0;
    total.value = 0;
    discountAmount.value = 0;
    appliedCouponCode.value = null;
    appliedPromotion.value = null;
    couponError.value = null;
    storage.clear();
    persistAppliedCoupon();
  };

  const applyCoupon = async (code: string) => {
    const normalizedCode = code.trim().toUpperCase();
    if (!normalizedCode || items.value.length === 0) {
      couponError.value = i18n.global.t('cart.coupon.error.invalid');
      return;
    }

    couponLoading.value = true;
    couponError.value = null;

    try {
      const result = await validatePromotion(getValidationPayload(normalizedCode));

      discountAmount.value = result.discount_amount;
      total.value = Math.max(subtotal.value - result.discount_amount, 0);
      appliedCouponCode.value = result.code;
      persistAppliedCoupon();

      const activePromotions = await getActivePromotions();
      appliedPromotion.value =
        activePromotions.find((promotion) => promotion.code === result.code) ?? null;

      toastStore.addToast(i18n.global.t('cart.coupon.success'), 'success');
    } catch (err: unknown) {
      couponError.value = mapCouponError(err);
      throw err;
    } finally {
      couponLoading.value = false;
    }
  };

  const removeCoupon = () => {
    appliedCouponCode.value = null;
    appliedPromotion.value = null;
    discountAmount.value = 0;
    couponError.value = null;
    recalculateTotal();
    persistAppliedCoupon();
  };

  const revalidateCouponIfNeeded = async () => {
    if (!appliedCouponCode.value) {
      return;
    }

    if (items.value.length === 0) {
      removeCoupon();
      return;
    }

    couponLoading.value = true;
    try {
      const result = await validatePromotion(getValidationPayload(appliedCouponCode.value));
      discountAmount.value = result.discount_amount;
      total.value = Math.max(subtotal.value - result.discount_amount, 0);
      couponError.value = null;
    } catch (_err: unknown) {
      removeCoupon();
      toastStore.addToast(i18n.global.t('cart.coupon.revalidationFailed'), 'info');
    } finally {
      couponLoading.value = false;
    }
  };

  // Sync local cart items to backend after login
  const syncLocalCartToBackend = async (): Promise<boolean> => {
    const authStore = useAuthStore();
    if (!authStore.token) return false;
    
    // Get local items that need to be synced
    const localItems = [...items.value];
    if (localItems.length === 0) return false;
    
    let syncedCount = 0;
    
    for (const item of localItems) {
      try {
        // Add each item to the backend
        await addUseCase.execute(
          item.productEntityType,
          item.productEntityId,
          item.quantity
        );
        syncedCount++;
      } catch (err) {
        console.error(`Failed to sync item ${item.id} to backend:`, err);
        // Continue with other items even if one fails
      }
    }
    
    // After syncing, reload cart from backend to get the correct state
    if (syncedCount > 0) {
      await loadCart();
      return true;
    }
    
    return false;
  };

  const updateState = (data: { items: CartItem[], subtotal: number, total: number }) => {
    items.value = data.items;
    subtotal.value = data.subtotal;
    total.value = data.total;
    recalculateTotal();
    
    // Persist como DTOs (shape compatible con backend)
    const dtos: CartItemDTO[] = data.items.map(item => ({
      id: item.id,
      product_entity_type: item.productEntityType,
      product_entity_id: item.productEntityId,
      quantity: item.quantity,
      unit_price: item.productData.price,
      subtotal: item.subtotal,
      is_purchable: item.isPurchable,
      product: {
        name: item.productData.name,
        image: item.productData.image,
        is_digital: item.productData.isDigital
      }
    }));
    storage.save(dtos);
  };

  const debouncedRevalidation = debounce(() => {
    if (appliedCouponCode.value) {
      void revalidateCouponIfNeeded();
    }
  }, 300);

  watch(items, () => {
    debouncedRevalidation();
  }, { deep: true });

  return {
    items,
    subtotal,
    total,
    discountAmount,
    appliedCouponCode,
    appliedPromotion,
    couponLoading,
    couponError,
    loading,
    error,
    itemsCount,
    isEmpty,
    init,
    loadCart,
    addItem,
    updateQuantity,
    removeItem,
    applyCoupon,
    removeCoupon,
    revalidateCouponIfNeeded,
    clearCart,
    syncLocalCartToBackend,
  };
});
